import { AbilitySocket, UniversalSocket } from "./ability_container.js"
import { Effect_container } from "./effect_container.js"
import { stat_depths } from "./stats_container.js"
import { Timer } from "./timer.js"
import { seconds_to_minutes_seconds } from "./math_functions.js"
import { Class, Subclass, Aspect, Fragment, Ability, Buff, Entity, Perk, Exotic_Armor, Exotic_Weapon, Activity, Armor_mod, Armor } from "./complex_effect.js"
import { update_armor_charge_duration, update_armor_charge_capacity } from "./interface_functions.js"

var class_names = ['titan', 'hunter', 'warlock'];

function normalize_stat(value)
{
    if (value < 0)
        value = 0;
    if (value > 100)
        value = 100;
    return value;
}

export class Character
{
    constructor(commander)
    {
        this.commander = commander;
        this.effects = new Effect_container(this);

        this.class = null;
        this.subclass = null;
        this.element = null;

        //this.stats = new Stats();

        this.armor_charge_durations = [ 10, 15, 18, 20 ];

        this.stats_ = {
            mobility: 0,
            resilience: 0,
            recovery: 0,
            discipline: 0,
            intellect: 0,
            strength: 0,
            fragment_energy_capacity: 0,
            armor_charge_capacity: 3, // max
            armor_charges: 0,         // current
            armor_charge_duration_tier: 0,
            one_armor_charge_duration: 10,
            total_armor_charge_duration: new Timer(),
            class_stat_name: ""
        };

        this.dependent_stats = {
            mobility: {
                walk_speed: 0,
                strafe_speed: 0,
                crouch_speed: 0,
                ability_regeneration_rate: 0
            },
            resilience: {
                shield_amount: 0,
                health_amount: 0,
                flinch_resistance: 0,
                resist_against_combatants: 0,
                ability_regeneration_rate: 0
            },
            recovery: {
                health_regen_delay: 0,
                shield_regen_delay: 0,
                health_regen_time: 0,
                shield_regen_time: 0,
                health_shield_regen_time: 0,
                total_regen_time: 0,
                ability_regeneration_rate: 0
            },
            discipline: {
                grenade_regeneration_rate: 0
            },
            intellect: {
                super_regeneration_rate: 0
            },
            strength: {
                melee_regeneration_rate: 0
            }
        }

        this.userStatBonuses = {
            mobility: 0,
            resilience: 0,
            recovery: 0,
            discipline: 0,
            intellect: 0,
            strength: 0,
        };

        this.sockets = {
            entries: {
                "aspect_0": new UniversalSocket(this, this.effects, 'aspect', 0),
                "aspect_1": new UniversalSocket(this, this.effects, 'aspect', 1),
                "fragment_0": new UniversalSocket(this, this.effects, 'fragment', 0),
                "fragment_1": new UniversalSocket(this, this.effects, 'fragment', 1),
                "fragment_2": new UniversalSocket(this, this.effects, 'fragment', 2),
                "fragment_3": new UniversalSocket(this, this.effects, 'fragment', 3),
                "fragment_4": new UniversalSocket(this, this.effects, 'fragment', 4),
                "fragment_5": new UniversalSocket(this, this.effects, 'fragment', 5),
                "super_ability": new AbilitySocket(this, this.effects, 'super_ability'),
                "class_ability": new AbilitySocket(this, this.effects, 'class_ability'),
                "melee_ability": new AbilitySocket(this, this.effects, 'melee_ability'),
                "grenade_ability": new AbilitySocket(this, this.effects, 'grenade_ability'),
                "exotic_armor":  new UniversalSocket(this, this.effects, 'exotic_armor'),
                "exotic_weapon": new UniversalSocket(this, this.effects, 'exotic_weapon'),
                "helmet": new UniversalSocket(this, this.effects, 'armor'),
                "gauntlets": new UniversalSocket(this, this.effects, 'armor'),
                "chest_armor": new UniversalSocket(this, this.effects, 'armor'),
                "leg_armor":  new UniversalSocket(this, this.effects, 'armor'),
                "class_armor":  new UniversalSocket(this, this.effects, 'armor'),
            },
            categories:
            {
                "aspect": ["aspect_0", "aspect_1"],
                "fragment": ["fragment_0", "fragment_1", "fragment_2", "fragment_3", "fragment_4", "fragment_5"],
                "grenade_ability": ["grenade_ability"],
                "super_ability": ["super_ability"],
                "class_ability": ["class_ability"],
                "melee_ability": ["melee_ability"],
                "exotic_armor":  ["exotic_armor"],
                "exotic_weapon": ["exotic_weapon"],
                "helmet": ["helmet"],
                "gauntlets": ["gauntlets"],
                "chest_armor": ["chest_armor"],
                "leg_armor": ["leg_armor"],
                "class_armor": ["class_armor"],
            }
        };

        this.sandbox = 'pvp';
        this.activity_id = null;

        this.dummy_stats = null;
        this.dummy_weapon_stats = null;

        this.traits = null;

        console.log('APP/CHAR: Character created');
    }

    set_class(class_id)
    {
        console.log('APP/CHAR: set class to', class_id);

        if (!class_names.includes(class_id))
        {
            console.log('wrong class store', class_id);
            class_id = 'titan';
        }

        this.class = class_id;

        switch (class_id)
        {
            case 'hunter':
                this.stats_.class_stat_name = 'mobility';
                break;
            case 'titan':
                this.stats_.class_stat_name = 'resilience';
                break;
            case 'warlock':
                this.stats_.class_stat_name = 'recovery';
                break;
        }

        // setting empty exotic armor
        this.set_exotic_armor('empty_exotic_armor_slot');

        this.commander.update(
            {
                update_type: "update_exotic_armor_selection",
            }
        );

        this.commander.update(
            {
                update_type: "save_class",
                class_id: this.class
            }
        );

        this.commander.update(
            {
                update_type: "update_subclass_buttons",
                element_to_subclass: this.get_effect(this.class).subclasses,
            }
        );
    }

    reset_class() {

        // reset_exotic_armor
    }

    set_subclass(subclass_id)
    {
        console.log('APP/Char: reset_subclass()');
        for (let i = 0; i < 2; i++)
        {
            this.plugInSocket('empty_aspect_socket_aspect', "aspect", i, false);
        }

        for (let i = 0; i < 6; i++)
        {
            let socketKey = this.sockets.categories["fragment"][i];
            this.plugInSocket('empty_fragment_socket_fragment', "fragment", i, false);
            this.sockets.entries[socketKey].deactivate();
        }

        //this.effects.dec_counter(this.subclass);
        this.effects.remove_item(this.subclass);

        console.log('APP/Char: set_subclass()', subclass_id);

        let subclass = this.effects.get(subclass_id);

        this.subclass = subclass_id;
        this.element = subclass.subclass_element;

        //this.effects.inc_counter(this.subclass);
        this.effects.add_item(this.subclass);

        this.plugInSocket(subclass.grenades[0], 'grenade_ability', 0, false);
        this.plugInSocket(subclass.class_abilities[0], 'class_ability', 0, false);
        this.plugInSocket(subclass.melees[0], 'melee_ability', 0, false);
        this.plugInSocket( subclass.supers[0], 'super_ability', 0, false);

        // updating state
        this.effects.apply_effects();
        this.calc_all_cooldowns();

        this.commander.update(
            {
                update_type: "update_menu_selections",
                subclass_id: this.subclass,
            }
        );

        this.commander.update(
            {
                update_type: "update_background",
                subclass_id: this.subclass,
            }
        );

        this.commander.update(
            {
                update_type: "update_stats_colors",
                subclass_element: this.element,
            }
        );

        this.commander.update(
            {
                update_type: "save_element",
                element: this.element
            }
        );
    }

    override_ability(socketCategory, newAbilityId)
    {
        let socketKey = this.sockets.categories[socketCategory][0];
        console.log("override ability: ",this.sockets.entries[socketKey].get_current(), " -> ", newAbilityId )

        this.sockets.entries[socketKey].override(newAbilityId);

        if (socketCategory == 'class_ability')
            this.calc_class_cooldown();
        else if (socketCategory == 'grenade_ability')
            this.calc_grenade_cooldown();
        else if (socketCategory == 'melee_ability')
            this.calc_melee_cooldown();
        else if (socketCategory == 'super_ability')
            this.calc_super_cooldown();
    }

    rollback_ability(socketCategory)
    {
        let socketKey = this.sockets.categories[socketCategory][0];
        this.sockets.entries[socketKey].rollback();

        // no apply effects because it is called only from apply_effects()

        if (socketCategory == 'class_ability')
            this.calc_class_cooldown();
        else if (socketCategory == 'grenade_ability')
            this.calc_grenade_cooldown();
        else if (socketCategory == 'melee_ability')
            this.calc_melee_cooldown();
        else if (socketCategory == 'super_ability')
            this.calc_super_cooldown();
    }

    set_exotic_armor(newExoticId, updating=true)
    {
        let socketKey = this.sockets.categories["exotic_armor"][0];
        this.sockets.entries[socketKey].set(newExoticId);

        if (updating)
        {
            this.effects.apply_effects();
            this.calc_all_cooldowns();
        }
    }

    plugInSocket(itemId, socketCategory, socketIndex, updating=true)
    {
        console.log("plug in socket", itemId, socketCategory, socketIndex)
        if (socketCategory == "aspect")
        {
            console.log("plug aspect")
            let socketKey = this.sockets.categories.aspect[socketIndex];

            let otherSocketIndex = - socketIndex + 1;
            let otherSocketKey = this.sockets.categories.aspect[otherSocketIndex];

            if (this.sockets.entries[otherSocketKey].get_current() == itemId)
            {
                this.sockets.entries[socketKey].swapValue(this.sockets.entries[otherSocketKey]);
                this.sockets.entries[otherSocketKey].set('empty_aspect_socket_aspect')
            }
            else
            {
                this.sockets.entries[socketKey].set(itemId)
            }

            // updates
            if (updating)
            {
                this.effects.apply_effects();
                this.calc_all_cooldowns();
            }
        }
        else if (socketCategory == "fragment")
        {
            console.log("plug fragment")
            let socketKey = this.sockets.categories.fragment[socketIndex];

            if (itemId == "empty_fragment_socket_fragment")
                this.sockets.entries[socketKey].set(itemId);
            else
            {
                let alreadyInserted = false;

                for (let otherSocketIndex = 0; otherSocketIndex < 6; otherSocketIndex++)
                {
                    let otherSocketKey = this.sockets.categories.fragment[otherSocketIndex];

                    if (this.sockets.entries[otherSocketKey].get_current() == itemId && otherSocketIndex != socketIndex)
                    {
                        this.sockets.entries[socketKey].swapValue(this.sockets.entries[otherSocketKey]);
                        this.sockets.entries[otherSocketKey].set('empty_fragment_socket_fragment')
                        alreadyInserted = true;
                        break;
                    }
                }

                if (!alreadyInserted)
                    this.sockets.entries[socketKey].set(itemId);
            }
            // updates
            if (updating)
            {
                this.effects.apply_effects();
                this.calc_all_cooldowns();
            }
        }
        else if (socketCategory == "super_ability" || socketCategory == "class_ability" || socketCategory == "grenade_ability" || socketCategory == "melee_ability")
        {
            console.log("plug ability")
            let socketKey = this.sockets.categories[socketCategory][0];
            this.sockets.entries[socketKey].set(itemId);

            if (updating)
            {
                this.effects.apply_effects();

                if (socketCategory == 'class_ability')
                    this.calc_class_cooldown();
                else if (socketCategory == 'grenade_ability')
                    this.calc_grenade_cooldown();
                else if (socketCategory == 'melee_ability')
                    this.calc_melee_cooldown();
                else if (socketCategory == 'super_ability')
                    this.calc_super_cooldown();
            }
        }
        else
        {
            //console.log("plug other", socketCategory, itemId);

            let socketKey = this.sockets.categories[socketCategory][0];
            this.sockets.entries[socketKey].set(itemId);

            if (updating)
            {
                this.effects.apply_effects();
                this.calc_all_cooldowns();
            }
        }
    }

    set_armor_charges(number)
    {
        this.stats_["armor_charges"] = number;

        this.stats_["total_armor_charge_duration"].set(this.stats_["armor_charges"] * this.stats_["one_armor_charge_duration"]);
        update_armor_charge_duration(this.stats_["total_armor_charge_duration"].get());
        this.effects.update_cooldown_effect_lists();

        this.effects.apply_effects();
        this.calc_all_cooldowns();
    }

    set_effect_stack(effect_id, value) // value [0 | 1, 2, 3, ..., N] | 0 - inactive, 1, 2, 3... active and have this stack
    {
        this.effects.set_stack(effect_id, value);

        if (value == 0)
        {
            this.effects.set_activity(effect_id, false);
            this.effects.active_effects.delete(this.get_effect(effect_id));
        }
        else
        {
            this.effects.set_activity(effect_id, true);
            this.effects.active_effects.add(this.get_effect(effect_id));
        }

        this.effects.apply_effects();
        this.calc_all_cooldowns();
        this.commander.update(
            {
                update_type: "update_effect_template",
                effect_id: effect_id
            }
        );

    }

    set_effect_duration(effect_id, value)
    {
        let effect = this.effects.get(effect_id);
        effect.set_duration(value);

        this.effects.update_cooldown_effect_lists();

        if (effect.is_active)
            this.calc_all_cooldowns();
    }

    set_ability_energy(energy_amount, socketCategory)
    {
        console.log('update', energy_amount);
        let socketKey = this.sockets.categories[socketCategory][0];
        this.sockets.entries[socketKey].energy = energy_amount;

        switch (socketCategory)
        {
            case 'grenade_ability':
                this.calc_grenade_cooldown();
                break;
            case 'melee_ability':
                this.calc_melee_cooldown();
                break;
            case 'class_ability':
                this.calc_class_cooldown();
                break;
            case 'super_ability':
                this.calc_super_cooldown();
                break;
        }
    }

    set_stat(stat_name, value)
    {
        console.log("set stat", stat_name, value);
        console.log("set stat, has", this.stats_[stat_name], 'bonus', this.userStatBonuses[stat_name])

        if (value <= this.stats_[stat_name])
            this.userStatBonuses[stat_name] = 0;
        else
            this.userStatBonuses[stat_name] = value - this.stats_[stat_name];

        console.log("set stat result, has", this.stats_[stat_name], 'bonus', this.userStatBonuses[stat_name])

        this.effects.apply_effects();

        switch (stat_name)
        {
            case 'discipline':
                this.calc_grenade_cooldown();
                break;
            case 'strength':
                this.calc_melee_cooldown();
                break;
            case this.stats_.class_stat_name:
                this.calc_class_cooldown();
                break;
            case 'intellect':
                this.calc_super_cooldown();
                break;
        }
    }

    set_sandbox(value)
    {
        this.sandbox = value;
        this.effects.apply_effects();
        this.calc_all_cooldowns();
    }

    set_activity_type(new_activity_id)
    {
        console.log("set_activity_type", new_activity_id)
        this.effects.remove_item(this.activity_id);
        this.activity_id = new_activity_id;
        this.effects.add_item(this.activity_id);
        this.effects.apply_effects();
        this.calc_all_cooldowns();
    }

    set_array_data(data)
    {
        for (let effect of data)
        {
            if (!this.effects.has(effect.id))
            {
                if (effect.type == 'activity')
                    this.effects.set(effect.id, new Activity(this, effect));
            }
        }
    }

    set_data(data, objectMode = false)  // puts json data -> complex_effects
    {
        if (!objectMode)
        {
            for (let [effect_id, effect_data] of Object.entries(data))
            {
                if (!this.effects.has(effect_id))
                {
                    //console.log(effect_id, effect_data)
                    if (effect_data.type == 'subclass')
                        this.effects.set(effect_id, new Subclass(this, effect_data));
                    else if (effect_data.type == 'class')
                        this.effects.set(effect_id, new Class(this, effect_data));
                    else if (effect_data.type == 'buff' || effect_data.type == 'debuff')
                        this.effects.set(effect_id, new Buff(this, effect_data));
                    else if (effect_data.type == 'entity')
                        this.effects.set(effect_id, new Entity(this, effect_data));
                    else if (effect_data.type == 'class_ability' || effect_data.type == 'grenade_ability' || effect_data.type == 'melee_ability' || effect_data.type == 'super_ability')
                        this.effects.set(effect_id, new Ability(this, effect_data));
                    else if (effect_data.type == 'aspect')
                        this.effects.set(effect_id, new Aspect(this, effect_data));
                    else if (effect_data.type == 'perk')
                        this.effects.set(effect_id, Perk.create(this, effect_data));
                    else if (effect_data.type == 'fragment')
                        this.effects.set(effect_id, new Fragment(this, effect_data));
                    else if (effect_data.type == 'exotic_weapon')
                        this.effects.set(effect_id, new Exotic_Weapon(this, effect_data));
                    else if (effect_data.type == 'exotic_armor')
                        this.effects.set(effect_id, new Exotic_Armor(this, effect_data));
                    else if (effect_data.type == 'armor_mod')
                        this.effects.set(effect_id, new Armor_mod(this, effect_data));
                    else if (effect_data.type == 'activity')
                        this.effects.set(effect_id, new Activity(this, effect_data));
                    else
                        console.log('set_data() encountered unknown type:', effect_data.type);
                }
            }
        }
        else
        {
            for (let [effect_id, effectObject] of Object.entries(data))
            {
                this.effects.set(effect_id, effectObject);
            }
        }
    }

    display_cooldown(ability, cooldown)
    {
        let socketKey = this.sockets.categories[ability][0]
        this.commander.update(
            {
                update_type: "update_displayed_cooldown",
                ability_id: this.sockets.entries[socketKey].get_current(),
                string_value: seconds_to_minutes_seconds(cooldown),
            }
        );
    }

    calc_all_cooldowns()
    {
        console.log('APP/CHAP: Subroutine calculate_all_cooldowns');
        this.calc_grenade_cooldown();
        this.calc_melee_cooldown();
        this.calc_class_cooldown();
        this.calc_super_cooldown();
    }

    calc_grenade_cooldown()
    {
        let socketKey = this.sockets.categories['grenade_ability'][0];

        if (this.sockets.entries[socketKey].get_current() != null)
        {
            this.display_cooldown(
                'grenade_ability',
                this.experimental_cd(
                    this.sockets.entries[socketKey],
                    'discipline',
                    'grenade_regen_rate',
                    'grenade_regeneration_rate'
                )
            );
        }
        console.log('APP/CHAP: Subroutine calculate_grenade_cooldown: Status==finished');
    }

    calc_melee_cooldown()
    {
        let socketKey = this.sockets.categories['melee_ability'][0];

        if (this.sockets.entries[socketKey].get_current() != null)
        {
            this.display_cooldown(
                'melee_ability',
                this.experimental_cd(
                    this.sockets.entries[socketKey],
                    'strength',
                    'melee_regen_rate',
                    'melee_regeneration_rate'
                )
            );
        }
        console.log('APP/CHAP: Subroutine calculate_melee_cooldown: Status==finished');
    }

    calc_class_cooldown()
    {
        let socketKey = this.sockets.categories["class_ability"][0];

        if (this.sockets.entries[socketKey].get_current() != null)
        {
            this.display_cooldown(
                'class_ability',
                this.experimental_cd(
                    this.sockets.entries[socketKey],
                    this.stats_.class_stat_name,
                    'class_regen_rate',
                    'ability_regeneration_rate'
                )
             );
        }
        console.log('APP/CHAP: Subroutine calculate_class_cooldown: Status==finished');
    }

    calc_super_cooldown()
    {
        let socketKey = this.sockets.categories["super_ability"][0];

        if (this.sockets.entries[socketKey].get_current() != null)
        {
            this.display_cooldown(
                'super_ability',
                this.experimental_cd(
                    this.sockets.entries[socketKey],
                    'intellect',
                    'super_regen_rate',
                    'super_regeneration_rate'
                )
            );
        }
        console.log('APP/CHAP: Subroutine calculate_super_cooldown: Status==finished');
    }

    experimental_cd(ability_container, stat_name, relation, minor_stat)
    {
        console.log("experimental_cd", ability_container, stat_name, relation, minor_stat);

        let ability_id = ability_container.get_current();

        console.log('Cooldown calculation for:', ability_id);

        let base_cooldown = ability_container.get_cooldown();
        let base_rate = 100 / base_cooldown;
        let have_energy = ability_container.energy;
        let need_energy = (100 - have_energy);

        console.log(`Base cooldown: ${base_cooldown}\nBase rate: ${base_rate} %/s\nHave energy: ${have_energy}%\nNeed energy: ${need_energy}%`);

        let const_scalar_sum = 0;
        let const_multiplier = 1;
        let const_stat_bonus = 0;

        console.log("List of effects with infinite duration:");
        for (let effect of this.effects.inf_duration)
        {
            console.log(effect);
            for (let simple_value of effect.get_simple_values(this.sandbox))
            {
                if (simple_value.get_type() == 'percent_increase' && simple_value.get_relates_to() == relation)
                {
                    const_scalar_sum += simple_value.get_value();
                    console.log('\t\t', relation, ': +', simple_value.get_value(), '%');
                }
                if (simple_value.get_type() == 'mul' && simple_value.get_relates_to() == relation)
                {
                    const_multiplier *= simple_value.get_value();
                    console.log('\t\t', relation, ': *', simple_value.get_value());
                }
                if (simple_value.get_type() == 'add' && simple_value.get_relates_to() == stat_name)
                {
                    const_stat_bonus += simple_value.get_value();
                    console.log('\t\t', stat_name, '+', simple_value.get_value());
                }
            }
        }

        const_scalar_sum /= 100; // because stored as percents
        console.log(`Const Scalar sum: ${const_scalar_sum}\nConst Multiplier: ${const_multiplier}\nConst Stat Bonus: ${const_stat_bonus}`);

        console.log("List of temporary effects:");
        for (let effect of this.effects.sorted_by_duration)
        {
            console.log(effect, `duration: ${effect.get_duration()}`);
        }

        let timestamp = 0;
        let cooldown = 0;
        let final_cooldown = 0;

        for (let i = 0; i < this.effects.sorted_by_duration.length; i++)
        {
            console.log('Iteration_start');
            if (this.effects.sorted_by_duration[i].get_duration() > timestamp)
            {
                let time_diff = this.effects.sorted_by_duration[i].get_duration() - timestamp;
                timestamp = this.effects.sorted_by_duration[i].get_duration();
                console.log(`new_timestamp ${this.effects.sorted_by_duration[i].get_duration()}, time diff from previous ${time_diff}`);

                let scalar_sum = 0;
                let multiplier = 1;
                let stat_bonus = 0;

                for (let j = i; j < this.effects.sorted_by_duration.length; j++)
                {
                    console.log(this.effects.sorted_by_duration);

                    for (let simple_value of this.effects.sorted_by_duration[j].get_simple_values(this.sandbox))
                    {
                        if (simple_value.get_type() == 'percent_increase' && simple_value.get_relates_to() == relation)
                        {
                            scalar_sum += simple_value.get_value();
                            console.log('\t\t', relation, ': +', simple_value.get_value(), '%');
                        }
                        if (simple_value.get_type() == 'mul' && simple_value.get_relates_to() == relation)
                        {
                            multiplier *= simple_value.get_value();
                            console.log('\t\t', relation, ': *', simple_value.get_value());
                        }
                        if (simple_value.get_type() == 'add' && simple_value.get_relates_to() == stat_name)
                        {
                            stat_bonus += simple_value.get_value();
                            console.log('\t\t', stat_name, '+', simple_value.get_value());
                        }
                    }
                }

                let stat_sum = normalize_stat(this.getStaWithBonus(stat_name) + const_stat_bonus + stat_bonus);
                let stat_tier = Math.floor(stat_sum  / 10);
                let stat_scalar = stat_depths[stat_name][minor_stat][stat_tier];
                console.log(`Iteration Stat scalar: ${stat_scalar}`);

                scalar_sum /= 100; // because stored as percents
                scalar_sum += stat_scalar;
                scalar_sum += const_scalar_sum;

                multiplier *= const_multiplier;
                let energy_gained = time_diff * scalar_sum * multiplier * base_rate;

                console.log(`Base Stat: ${this.getStaWithBonus(stat_name)}, Const Bonus: ${const_stat_bonus}, Iteration Bonus: ${stat_bonus}, Total: ${stat_sum}, Stat Tier ${stat_tier}`);
                console.log(`Scalar sum: ${scalar_sum}, Multiplier: ${multiplier}, Need_energy: ${need_energy}, Energy Gained: ${energy_gained}`);

                if (need_energy > energy_gained)
                {
                    console.log('to next round!');
                    need_energy -= energy_gained;
                    cooldown = timestamp;
                }
                else
                {
                    console.log('amount of energy gained is more than needed, so need to calculate final time (1)');
                    let final_round_time = need_energy / scalar_sum / multiplier / base_rate;
                    final_cooldown = cooldown + final_round_time;
                    console.log(`time of final calculation round: ${final_round_time}, result time: ${final_cooldown}`);
                    need_energy = 0;
                    break;
                }
            }
        }
        if (need_energy > 0)
        {
            let stat_sum = normalize_stat(this.getStaWithBonus(stat_name) + const_stat_bonus);
            let stat_tier = Math.floor(stat_sum  / 10);
            console.log(stat_name, minor_stat, stat_tier)
            let stat_scalar = stat_depths[stat_name][minor_stat][stat_tier];

            let scalar_sum = const_scalar_sum + stat_scalar;
            let multiplier = const_multiplier;

            console.log('final round (1) was not reached, starting final round (2), only inf_duration effects and stat_scalar are used');
            console.log(`Base Stat: ${this.getStaWithBonus(stat_name)}, Const Bonus: ${const_stat_bonus}, Total: ${stat_sum}, Stat Tier ${stat_tier}`);
            console.log(`Scalar sum ${scalar_sum}, Multiplier ${multiplier}, need_energy ${need_energy}`);

            let final_round_time = need_energy / scalar_sum / multiplier / base_rate;
            final_cooldown = cooldown + final_round_time;
            console.log(`time of final calculation round: ${final_round_time}, result time: ${final_cooldown}`);
        }

        return Math.round(final_cooldown);
    }

    // top-level
    is_action_fulfilled(action)
    {
        if (action.type == 'override_ability')
        {
            let socketKey = this.sockets.categories[action.override_target][0];
            console.log("action fulfilled:", this.sockets.entries[socketKey].get_current() == action.override_value)
            return this.sockets.entries[socketKey].get_current() == action.override_value;
        }
        else if (action.type == 'override_ability_cooldown')
        {
            let socketKey = this.sockets.categories[action.override_target][0];
            console.log("action fulfilled:", this.sockets.entries[socketKey].get_cooldown() == action.override_value)
            return this.sockets.entries[socketKey].get_cooldown() == action.override_value;
        }
    }

    check_condition(condition)
    {
        if (condition == null || condition === undefined)
        {
            return true;
        }
        console.log('APP/CHAR: Subroutine: check_condition():', condition.operator, condition.left, condition.right);

        switch (condition.operator)
        {
            case 'or':
                return this.check_condition(condition.left) || this.check_condition(condition.right);
            case 'and':
                return this.check_condition(condition.left) && this.check_condition(condition.right);
            case 'not':
                return !(this.check_condition(condition.left));
            case '>':
                console.log((this.check_condition(condition.left) > this.check_condition(condition.right)))
                return (this.check_condition(condition.left) > this.check_condition(condition.right));
            case '<':
                console.log((this.check_condition(condition.left) < this.check_condition(condition.right)))
                return (this.check_condition(condition.left) < this.check_condition(condition.right));
            case 'ability_match_id':
            {
                let socketKey = this.sockets.categories[condition.left][0];
                return this.sockets.entries[socketKey].get_current() == condition.right;
            }
            case 'absolute_ability_match_id':
            {
                let socketKey = this.sockets.categories[condition.left][0];
                return this.sockets.entries[socketKey].get_default() == condition.right;
            }
            case 'effect_active':
                console.log(this.is_active(condition.left));
                return this.is_active(condition.left);
            case "cooldown_matches":
                let socketKey = this.sockets.categories[condition.left][0];
                return this.sockets.entries[socketKey].get_cooldown() == condition.right;
            case 'sandbox_equals':
                return this.sandbox == condition.left;
            case 'super_element_match':
                return this.get_super_element() == condition.left;
            case 'ability_element_match':
            {
                let socketKey = this.sockets.categories[condition.left][0];
                return this.sockets.entries[socketKey].get_element() == condition.right;
            }
            case 'get_effect_stack':
                return this.get_effect_stack(condition.left);
            case 'num':
                return parseFloat(condition.left);
            case "class_match":
                return this.class == condition.left;
            case "armor_charges_active":
                return (this.stats_["armor_charges"] > 0);
        }
    }

    calculate_formula(formula)
    {
        console.log('APP/CHAR: Subroutine: calculate_formula():', formula);
        if (formula == null)
        {
            return true;
        }

        switch (formula.operator)
        {
            case 'sub':
                return this.calculate_formula(formula.left) - this.calculate_formula(formula.right);
            case 'sum':
                console.log(this.calculate_formula(formula.left) + this.calculate_formula(formula.right));
                return this.calculate_formula(formula.left) + this.calculate_formula(formula.right);
            case 'mul':
                console.log(this.calculate_formula(formula.left) * this.calculate_formula(formula.right));
                return this.calculate_formula(formula.left) * this.calculate_formula(formula.right);
            case 'div':
                return this.calculate_formula(formula.left) / this.calculate_formula(formula.right);
            case 'num':
                return parseFloat(formula.left);
            case 'get_major_stat':
                return this.getStaWithBonus(formula.left);
            case 'get_effect_stack':
                console.log(this.get_effect_stack(formula.left));
                return this.get_effect_stack(formula.left);
            case "get_super_element":
                console.log("get_super_element", this.get_super_element())
                return this.get_super_element();
            case "random_int":
                let r = Math.floor(Math.random() * (formula.right - formula.left) + formula.left);

                console.log("random:", r, formula)
                return r;

        }
    }

    updateStats(statsChanges)
    {
        console.log("character: updateStats", statsChanges);

        for (let statId in statsChanges)
        {
            if (statsChanges[statId] != 0)
            {
                console.log('stat changed', statId, statsChanges[statId]);

                switch (statId)
                {
                    case "mobility":
                    case "resilience":
                    case "recovery":
                    case "discipline":
                    case "intellect":
                    case "strength":
                        this.stats_[statId] += statsChanges[statId];
                        break;
                    case "fragment_energy_capacity":
                        console.log(
                            "character: fragment energy capacity changed, current value:",
                            this.stats_["fragment_energy_capacity"], "change: ", statsChanges[statId]
                        );
                        
                        let oldCapacity = this.stats_["fragment_energy_capacity"];
                        this.stats_["fragment_energy_capacity"] += statsChanges[statId];

                        if (this.stats_["fragment_energy_capacity"] > oldCapacity)
                        {
                            for (let i = oldCapacity; i < this.stats_["fragment_energy_capacity"]; i++)
                            {
                                console.log("activating fragment:", i);
                                this.sockets.entries[`fragment_${i}`].activate();
                            }
                        }
                        else if (this.stats_["fragment_energy_capacity"] < oldCapacity)
                        {
                            for (let i = this.stats_["fragment_energy_capacity"]; i < oldCapacity; i++)
                            {
                                console.log("blocking fragment:", i);
                                this.sockets.entries[`fragment_${i}`].deactivate();
                            }
                        }
                        break;
                    case "armor_charge_duration_tier":
                        console.log("armor_charge_duration_tier", statsChanges[statId]);

                        this.stats_["armor_charge_duration_tier"] += statsChanges[statId];
                        this.stats_["one_armor_charge_duration"] = this.armor_charge_durations[this.stats_["armor_charge_duration_tier"]];

                        this.stats_["total_armor_charge_duration"].set(this.stats_["armor_charges"] * this.stats_["one_armor_charge_duration"]);
                        update_armor_charge_duration(this.stats_["total_armor_charge_duration"].get());

                        this.effects.update_cooldown_effect_lists();
                        break;
                    case "armor_charge_capacity":
                        this.stats_["armor_charge_capacity"] += statsChanges[statId];

                        if (this.stats_["armor_charges"] > this.stats_["armor_charge_capacity"])
                            this.set_armor_charges(this.stats_["armor_charge_capacity"]);

                        update_armor_charge_capacity(this.stats_["armor_charge_capacity"]);
                        break;
                }
            }
        }
    }

    get_super_element()
    {
        let socketKey = this.sockets.categories['super_ability'][0];

        if (this.sockets.entries[socketKey].get_current() == null)
        {
            console.log('APP/CHAR: Subroutine: get_super_element found no super:');
            return 'noelement'
        }
        console.log('APP/CHAR: Subroutine: get_super_element returns:', this.effects.get(this.sockets.entries[socketKey].get_current()).element);

        return this.effects.get(this.sockets.entries[socketKey].get_current()).element;
    }

    get_template(effect_id) // returns certain effect's template for specified gamemode
    {
        return this.effects.get(effect_id).get_template(this.sandbox);
    }

    get_effect(effect_id)
    {
        return this.effects.get(effect_id);
    }

    get_effect_stack(effect_id)
    {
        return this.effects.get(effect_id).current_stack;
    }

    is_active(effect_id) // checks whether the effect with this id active
    {
        if (this.effects.has(effect_id))
        {
            return this.effects.get(effect_id).is_active;
        }
        return false; // if effect is not present so it is inactive
    }

    get_stat(stat_name)
    {
        return this.stats_[stat_name];
    }

    getUserStatBonus(stat_name)
    {
        return this.userStatBonuses[stat_name];
    }

    getStaWithBonus(stat_name)
    {
        let res = this.stats_[stat_name] + this.userStatBonuses[stat_name];

        return res;
    }

    get_effect(value)
    {
        return this.effects.get(value);
    }

    get_armor_charges()
    {
        return this.stats_["armor_charges"];
    }

    get_sandbox()
    {
        return this.sandbox;
    }

    get_exotic_armor_list()
    {
        return this.get_effect(this.class).exotic_armor;
    }

    get_armor_charge_timer()
    {
        return this.stats_["total_armor_charge_duration"];
    }
}
